import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import { Plus, RefreshCw, Upload, Loader2, Trash2, Search, Building2, AlertTriangle, Pencil, Archive, ArchiveRestore, X } from "lucide-react";
import { toast } from "sonner";

const TransactionsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [direction, setDirection] = useState(searchParams.get("direction") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [plaid, setPlaid] = useState({ enabled: false });
  const [linkToken, setLinkToken] = useState(null);
  // Manual entry
  const [showAdd, setShowAdd] = useState(false);
  const [m_amount, setMAmount] = useState("");
  const [m_direction, setMDirection] = useState("in");
  const [m_date, setMDate] = useState(new Date().toISOString().slice(0, 10));
  const [m_name, setMName] = useState("");
  const [m_category, setMCategory] = useState("");
  // Edit modal
  const [editing, setEditing] = useState(null);
  const [e_amount, setEAmount] = useState("");
  const [e_direction, setEDirection] = useState("in");
  const [e_date, setEDate] = useState("");
  const [e_name, setEName] = useState("");
  const [e_category, setECategory] = useState("");
  const [e_notes, setENotes] = useState("");
  const [e_account, setEAccount] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (direction) params.set("direction", direction);
      if (category) params.set("category", category);
      if (search.trim()) params.set("search", search.trim());
      if (showArchived) params.set("include_archived", "true");
      const { data } = await apiClient.get(`/finance/transactions?${params.toString()}`);
      setItems(data.items);
      setCategories(data.categories);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [direction, category, search, showArchived]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/finance/plaid/status");
        setPlaid(data);
        if (data.enabled) {
          const t = await apiClient.post("/finance/plaid/link-token");
          setLinkToken(t.data.link_token);
        }
      } catch { /* dormant */ }
    })();
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token) => {
      try {
        await apiClient.post("/finance/plaid/exchange", { public_token });
        toast.success("Bank connected — syncing…");
        await sync();
      } catch (err) { toast.error(formatApiError(err)); }
    },
  });

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/finance/plaid/sync");
      toast.success(`Synced: +${data.added} added, ${data.modified} updated, ${data.removed} removed`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!m_amount || !m_name) return toast.error("Amount and description required");
    setBusy(true);
    try {
      await apiClient.post("/finance/transactions", {
        amount: Number(m_amount),
        direction: m_direction,
        date: m_date,
        name: m_name,
        category: m_category || null,
      });
      toast.success("Transaction added");
      setMAmount(""); setMName(""); setMCategory("");
      setShowAdd(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeTx = async (id) => {
    if (!window.confirm("Void this transaction? Any 10% reserve tied to it will also be voided.")) return;
    try {
      await apiClient.delete(`/finance/transactions/${id}`);
      toast.success("Voided");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (tx) => {
    setEditing(tx);
    setEAmount(String(tx.amount));
    setEDirection(tx.direction);
    setEDate(tx.date);
    setEName(tx.name || "");
    setECategory(tx.category || "");
    setENotes(tx.notes || "");
    setEAccount(tx.account || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      await apiClient.patch(`/finance/transactions/${editing.id}`, {
        amount: Number(e_amount),
        direction: e_direction,
        date: e_date,
        name: e_name,
        category: e_category,
        notes: e_notes,
        account: e_account,
      });
      toast.success("Transaction updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const archiveTx = async (tx) => {
    try {
      await apiClient.post(`/finance/transactions/${tx.id}/${tx.archived ? "unarchive" : "archive"}`);
      toast.success(tx.archived ? "Restored" : "Archived");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/finance/transactions/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} rows${data.errors.length ? ` (${data.errors.length} skipped)` : ""}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <FinanceLayout>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="mono-label mb-2">// LEDGER</div>
          <h1 className="serif-head text-3xl">Transactions</h1>
          <p className="text-nex-chrome mt-1 text-sm">
            Every incoming revenue line auto-triggers a 10% Working Capital Reserve hold
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {plaid.enabled ? (
            <button onClick={() => open()} disabled={!ready} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="plaid-connect-btn">
              <Building2 size={13} /> Connect bank
            </button>
          ) : (
            <span className="mono-body text-[10px] text-amber-300 inline-flex items-center gap-1" data-testid="plaid-dormant-badge">
              <AlertTriangle size={12} /> Plaid dormant
            </span>
          )}
          {plaid.enabled && (
            <button onClick={sync} disabled={busy} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="plaid-sync-btn">
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Sync
            </button>
          )}
          <label className="tick-btn-secondary text-xs !py-2 !px-3 cursor-pointer" data-testid="csv-import-label">
            <Upload size={13} /> Import CSV
            <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="csv-input" />
          </label>
          <button onClick={() => setShowAdd((s) => !s)} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="add-tx-btn">
            <Plus size={13} /> Add
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addManual} className="card-industrial corner-tick p-5 mb-6 grid md:grid-cols-6 gap-3" data-testid="add-tx-form">
          <input type="number" step="0.01" value={m_amount} onChange={(e) => setMAmount(e.target.value)} placeholder="Amount" className="input-industrial" data-testid="tx-amount" />
          <select value={m_direction} onChange={(e) => setMDirection(e.target.value)} className="input-industrial" data-testid="tx-direction">
            <option value="in">Income</option>
            <option value="out">Expense</option>
          </select>
          <input type="date" value={m_date} onChange={(e) => setMDate(e.target.value)} className="input-industrial" data-testid="tx-date" />
          <input type="text" value={m_name} onChange={(e) => setMName(e.target.value)} placeholder="Description" className="input-industrial md:col-span-2" data-testid="tx-name" />
          <select value={m_category} onChange={(e) => setMCategory(e.target.value)} className="input-industrial" data-testid="tx-category">
            <option value="">Auto-categorize</option>
            {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button type="submit" disabled={busy} className="tick-btn-primary md:col-span-6 justify-center" data-testid="tx-save">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Save transaction"}
          </button>
        </form>
      )}

      <div className="grid md:grid-cols-3 gap-3 mb-2" data-testid="tx-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description or notes" className="input-industrial !pl-9" data-testid="tx-search" />
        </div>
        <select value={direction} onChange={(e) => setDirection(e.target.value)} className="input-industrial" data-testid="filter-direction">
          <option value="">All directions</option>
          <option value="in">Income only</option>
          <option value="out">Expense only</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-industrial" data-testid="filter-category">
          <option value="">All categories</option>
          {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {user?.role === "partner" && (
        <div className="mb-4 flex items-center gap-3">
          <label className="flex items-center gap-2 text-nex-chrome text-xs cursor-pointer select-none">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="tx-show-archived" className="accent-nex-gold" />
            Show archived
          </label>
          <span className="mono-body text-[10px] uppercase tracking-widest text-nex-gold">// PARTNER MODE · EDIT / ARCHIVE / DELETE ANY ROW</span>
        </div>
      )}

      <div className="card-industrial corner-tick overflow-hidden" data-testid="tx-table">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-nex-chrome text-sm">No transactions yet. Import CSV or Add above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/30 border-b border-nex-border">
                <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((tx) => (
                  <tr key={tx.id} className={`border-b border-nex-border/50 hover:bg-white/[0.02] ${tx.archived ? "opacity-50" : ""}`} data-testid={`tx-row-${tx.id}`}>
                    <td className="px-4 py-3 mono-body text-[11px] text-nex-chrome whitespace-nowrap">{tx.date}</td>
                    <td className="px-4 py-3">
                      <div className="text-nex-silver flex items-center gap-2">
                        {tx.name}
                        {tx.archived && <span className="mono-body text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-nex-border/40 text-nex-muted">Archived</span>}
                      </div>
                      {tx.notes && <div className="text-nex-muted text-xs mt-1">{tx.notes}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="mono-body text-[10px] uppercase tracking-[0.1em] text-nex-gold">{categories[tx.category] || tx.category}</span>
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${tx.direction === "in" ? "text-emerald-300" : "text-nex-chrome"}`}>
                      {tx.direction === "in" ? "+" : "−"}{fmtUsd(tx.amount)}
                    </td>
                    <td className="px-4 py-3 mono-body text-[10px] uppercase text-nex-muted">{tx.source}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {user?.role === "partner" && (
                        <>
                          <button onClick={() => openEdit(tx)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`edit-tx-${tx.id}`} title="Edit"><Pencil size={14} /></button>
                          <button onClick={() => archiveTx(tx)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`archive-tx-${tx.id}`} title={tx.archived ? "Restore" : "Archive"}>
                            {tx.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                          </button>
                        </>
                      )}
                      <button onClick={() => removeTx(tx.id)} className="text-nex-muted hover:text-red-300" data-testid={`delete-tx-${tx.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setEditing(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-lg px-4" data-testid="edit-tx-modal">
            <div className="card-industrial corner-tick p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif-head text-xl">Edit Transaction</h3>
                <button onClick={() => setEditing(null)} className="text-nex-muted hover:text-nex-silver" data-testid="edit-tx-close"><X size={18} /></button>
              </div>
              <form onSubmit={saveEdit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Amount ($)</div>
                    <input type="number" step="0.01" value={e_amount} onChange={(ev) => setEAmount(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-amount" />
                  </label>
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Direction</div>
                    <select value={e_direction} onChange={(ev) => setEDirection(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-direction">
                      <option value="in">Income</option>
                      <option value="out">Expense</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Date</div>
                    <input type="date" value={e_date} onChange={(ev) => setEDate(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-date" />
                  </label>
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Category</div>
                    <select value={e_category} onChange={(ev) => setECategory(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-category">
                      {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Description</div>
                  <input type="text" value={e_name} onChange={(ev) => setEName(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-name" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Account</div>
                  <input type="text" value={e_account} onChange={(ev) => setEAccount(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-account" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Notes</div>
                  <input type="text" value={e_notes} onChange={(ev) => setENotes(ev.target.value)} className="input-industrial w-full" data-testid="edit-tx-notes" />
                </label>
                <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center mt-2" data-testid="edit-tx-save">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
};

export default TransactionsPage;
